import type { ComponentProps } from "react";
import { Pressable, StyleSheet, type PressableProps } from "react-native";

import { radii, useThemedStyles, type ThemeColors } from "~/styles/theme";
import { Icon } from "./Icon";

type IconButtonProps = Omit<PressableProps, "children" | "style"> & {
  icon: ComponentProps<typeof Icon>["name"];
  label: string;
  size?: "sm" | "md" | "lg";
  variant?: "solid" | "outline" | "ghost" | "danger";
};

export function IconButton({
  icon,
  label,
  size = "md",
  variant = "ghost",
  disabled,
  ...props
}: IconButtonProps) {
  const { stylesheet, sizeStyles, variantStyles } = useThemedStyles(makeStyles);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled: Boolean(disabled) }}
      disabled={disabled}
      style={({ hovered, pressed }) => [
        stylesheet.button,
        sizeStyles[size],
        variantStyles[variant],
        hovered || pressed ? stylesheet.hovered : null,
        disabled ? stylesheet.disabled : null,
      ]}
      {...props}
    >
      <Icon
        name={icon}
        size={size}
        tone={
          variant === "solid"
            ? "primary"
            : variant === "danger"
              ? "danger"
              : "default"
        }
      />
    </Pressable>
  );
}

const makeStyles = (colors: ThemeColors) => {
  const stylesheet = StyleSheet.create({
    button: {
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      borderRadius: radii.pill,
    },
    sm: { width: 32, height: 32 },
    md: { width: 40, height: 40 },
    lg: { width: 44, height: 44 },
    solid: { backgroundColor: colors.primary },
    outline: {
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.background,
    },
    ghost: { backgroundColor: "transparent" },
    danger: {
      borderWidth: 1,
      borderColor: colors.danger,
      backgroundColor: colors.background,
    },
    hovered: { backgroundColor: colors.surface },
    disabled: { opacity: 0.5 },
  });
  const sizeStyles = {
    sm: stylesheet.sm,
    md: stylesheet.md,
    lg: stylesheet.lg,
  };
  const variantStyles = {
    solid: stylesheet.solid,
    outline: stylesheet.outline,
    ghost: stylesheet.ghost,
    danger: stylesheet.danger,
  };
  return { stylesheet, sizeStyles, variantStyles };
};
